import { useContext, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import PageHeader from "../PageHeader";
import ProjectModal from "./ProjectModal";
import { displayName } from "./userDisplay";
import useProjects from "../../hooks/useProjects";
import useMe from "../../hooks/useMe";
import AuthContext from "../../context/AuthContext";

// Where a project's tasks live: the board, filtered to it (board #83).
const boardPath = (project) => `/tasks/board?project=${project.id}`;

function ProjectList() {
  const auth = useContext(AuthContext);
  const me = auth?.user?.user_id ?? null;
  const { me: account } = useMe();
  const {
    projects,
    loading,
    error,
    refresh,
    createProject,
    updateProject,
    deleteProject,
  } = useProjects();

  const [modalProject, setModalProject] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const handleOpenCreate = () => {
    setModalProject(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (project) => {
    setModalProject(project);
    setModalOpen(true);
  };

  const handleCloseModal = () => setModalOpen(false);

  const handleSave = async (data) => {
    if (modalProject) {
      await updateProject(modalProject.id, data);
    } else {
      await createProject(data);
    }
  };

  const handleRequestDelete = (project) => {
    setDeleteTarget(project);
    setDeleteError(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteProject(deleteTarget.id);
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError(err?.message || "Failed to delete project");
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    if (!deleting) {
      setDeleteTarget(null);
      setDeleteError(null);
    }
  };

  return (
    <div className="page-container mt-6 pt-4">
      <div className="flex items-start justify-between gap-4">
        <PageHeader
          title="Projects"
          subtitle="Group related tasks into projects — yours, plus the ones others share with you or your household"
        />
        <Button className="shrink-0" onClick={handleOpenCreate}>
          New project
        </Button>
      </div>

      {loading && (
        <div className="py-12 text-center" role="status">
          <Loader2 className="inline size-6 animate-spin text-muted-foreground" />
          <span className="sr-only">Loading projects...</span>
        </div>
      )}

      {!loading && error && (
        <Alert variant="destructive">
          <AlertDescription className="flex items-center gap-3">
            <span className="flex-1">
              Failed to load projects. Please try again.
            </span>
            <Button size="sm" variant="outline" onClick={refresh}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {!loading && !error && projects.length === 0 && (
        <div className="py-12 text-center text-muted-foreground">
          No projects yet. Create one to start grouping tasks.
        </div>
      )}

      {!loading && !error && projects.length > 0 && (
        <div className="flex flex-col gap-2">
          {projects.map((project) => {
            const ownerId = project.user?.id ?? null;
            const mine = me == null || ownerId == null || ownerId === me;
            return (
              <div
                key={project.id}
                className="flex flex-col gap-3 rounded-lg border bg-card p-4 text-card-foreground shadow-xs sm:flex-row sm:items-start sm:justify-between sm:gap-4"
              >
                <div className="min-w-0 flex-1">
                  <h3 className="mb-1 flex flex-wrap items-center gap-2 font-display text-lg font-semibold">
                    <Link
                      to={boardPath(project)}
                      className="text-foreground underline-offset-4 hover:underline"
                    >
                      {project.name}
                    </Link>
                    {project.visibility === "household" && (
                      <>
                        {" "}
                        <Badge variant="secondary" className="font-normal">
                          Household
                        </Badge>
                      </>
                    )}
                    {project.visibility === "people" && (
                      <>
                        {" "}
                        <Badge
                          variant="secondary"
                          className="font-normal"
                          title={(project.shared_with || [])
                            .map(displayName)
                            .join(", ")}
                        >
                          Shared with {(project.shared_with || []).length}
                        </Badge>
                      </>
                    )}
                  </h3>
                  {!mine && project.user && (
                    <div className="mb-1 text-sm text-muted-foreground">
                      Shared by {displayName(project.user)}
                    </div>
                  )}
                  {project.description && (
                    <p className="m-0 text-muted-foreground">
                      {project.description}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
                  <Button size="sm" variant="outline" asChild>
                    <Link
                      to={boardPath(project)}
                      aria-label={`Open the ${project.name} board`}
                    >
                      Open board
                    </Link>
                  </Button>
                  {mine ? (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        aria-label={`Edit ${project.name}`}
                        onClick={() => handleOpenEdit(project)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                        aria-label={`Delete ${project.name}`}
                        onClick={() => handleRequestDelete(project)}
                      >
                        Delete
                      </Button>
                    </>
                  ) : (
                    <span className="text-sm text-muted-foreground">
                      Only the owner can edit
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ProjectModal
        show={modalOpen}
        onHide={handleCloseModal}
        project={modalProject}
        onSave={handleSave}
        canShareWithPeople={account?.kind === "human"}
      />

      {/* The delete confirm. A Dialog (role=dialog) rather than AlertDialog
          so the page's tests and screen-reader announcements stay as they
          were; clicking outside never closes it (static backdrop). */}
      <Dialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => !open && handleCancelDelete()}
      >
        <DialogContent
          showCloseButton={!deleting}
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => deleting && event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              Delete project
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{" "}
              <strong>{deleteTarget?.name}</strong>?
            </DialogDescription>
          </DialogHeader>
          {deleteError && (
            <Alert variant="destructive">
              <AlertDescription>{deleteError}</AlertDescription>
            </Alert>
          )}
          <Alert>
            <AlertDescription>
              This will also delete <strong>all tasks</strong> that belong to
              this project. This action cannot be undone.
            </AlertDescription>
          </Alert>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={handleCancelDelete}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <Loader2 className="animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ProjectList;
